import React, { Component } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Notification from './Notification';
import Navigation from './Navigation';
import Search from './Search';
import Cart from '../Selection/Cart';
import { translate, prefix } from '../../utils/locale';
import { addEvent, removeEvent, hasClass, addClass, removeClass, offset, scrollTo } from '../../utils/dom';
import { removeNotification, clearFilter, changeLanguage, changeCountry, goToPage, changeDevice } from '../../actions/environment';
import { clientInit, changeAddressInput } from '../../actions/checkout';
import s from './Header.scss';

let stickyOffset = 55; // tablet, desktop 52
const breakPoints = [900, 600];
const media = { w_600: 'mobile', w_900: 'tablet', w_1200: 'desktop' };

class Header extends Component {
	constructor(props) {
		super(props);

		this.state = {
			location: false,
			grid: false,
			menuPopupHeight: 'auto',
			cartOpen: false,
			cartClose: false,
			showSearch: false,
			showProgress: false,
		};

		this.headerElement = React.createRef();
		this.linksElement = React.createRef();
		this.cartElement = React.createRef();
		this.languageCountryElement = React.createRef();
	}

	componentDidMount() {
		if (typeof window !== 'undefined') {
			const $html = document.querySelector('html');
			let currentWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
			let device = media[`w_${currentWidth}`];

			this.props.clientInit();
			this.props.changeDevice(device);
			if (device === 'tablet' || device === 'desktop') stickyOffset = 70;
			else stickyOffset = 55;

			// Wait for page to be rendered with all relevant elements.
			const initiateSiteListeners = () => {
				if (!this.headerElement || !this.linksElement || !this.cartElement) {
					setTimeout(initiateSiteListeners, 200);
				}

				let isScrolled = false;
				let headerIsFixed = false;
				let isScrollingUp = false;
				let lastScrolledDown = 0;
				let lastScrolledUp = 1e5;

				this.setState({
					menuPopupHeight: window.innerHeight - stickyOffset,
				});

				const resizeEvent = () => {
					const updatedWidth = breakPoints.reduce((acc, bp) => (bp > window.innerWidth ? bp : acc), 1200);
					this.setState({ menuOffset: offset(this.headerElement.current).top + stickyOffset });

					if (this.state.menuOffset !== window.innerHeight - stickyOffset) {
						this.setState({
							menuPopupHeight: window.innerHeight - stickyOffset,
						});
					}

					// Switch between mobile, table and desktop.
					if (currentWidth !== updatedWidth) {
						device = media[`w_${updatedWidth}`];
						this.props.changeDevice(device);
						if (device === 'tablet' || device === 'desktop') stickyOffset = 70;
						else stickyOffset = 55;
						currentWidth = updatedWidth;
					}
				};

				addEvent(window, 'resize.logoHeader', () => {
					window.requestAnimationFrame(resizeEvent);
				});

				const scrollEvent = () => {
					const top = window.pageYOffset || document.documentElement.scrollTop;
					if (this.state.menuOffset !== window.innerHeight - stickyOffset) {
						this.setState({
							menuPopupHeight: window.innerHeight - stickyOffset,
						});
					}

					// make logo sticky when level with header.
					if (!headerIsFixed && top >= this.state.menuOffset) {
						headerIsFixed = true;
						addClass($html, 'u_headerIsFixed');
						this.setState({ location: false });
					} else if (headerIsFixed && top < this.state.menuOffset - stickyOffset) {
						headerIsFixed = false;
						removeClass($html, 'u_headerIsFixed');
					}

					if (top > 0 && !isScrolled) {
						addClass($html, 'u_pageIsScrolled');
						isScrolled = true;
					} else if (top === 0) {
						removeClass($html, 'u_pageIsScrolled');
						removeClass($html, 'u_headerIsFixed');
						removeClass($html, 'u_pageIsScrollingUp');
						isScrolled = false;
						headerIsFixed = false;
					}

					if (top > lastScrolledDown) lastScrolledDown = top;
					if ((top < lastScrolledUp && isScrollingUp) || !isScrollingUp) lastScrolledUp = top;

					// console.log('DEBUG: lastScrolledDown, lastScrolledUp, top', lastScrolledDown, lastScrolledUp, top, isScrollingUp);

					if (lastScrolledDown - top > 50 && !isScrollingUp) {
						addClass($html, 'u_pageIsScrollingUp');
						isScrollingUp = true;
					}
					if (top - lastScrolledUp > 50 && isScrollingUp) {
						removeClass($html, 'u_pageIsScrollingUp');
						this.setState({ location: false });
						lastScrolledDown = top;
						isScrollingUp = false;
					}
				};

				addEvent(window, 'scroll.logoHeader', () => {
					window.requestAnimationFrame(scrollEvent);
				});
			};

			initiateSiteListeners();

			if ('ontouchstart' in document.documentElement) {
				removeClass($html, 'no-touchevents');
				addClass($html, 'touchevents');
			}

			if (hasClass($html, 'no-touchevents')) {
				addEvent(window, 'touchstart.touchTest', () => {
					removeClass($html, 'no-touchevents');
					addClass($html, 'touchevents');
					removeEvent(window, 'touchstart.touchTest');
				});
			}

			addEvent(document.getElementById('root'), 'click.linkCapture', (e) => {
				const $link = e.target.closest('a');
				const linkLocation = ($link && $link.getAttribute('href')) || '';

				if ($link && hasClass($link, 'linkCapture')) {
					e.preventDefault();
					if (linkLocation.indexOf('#') === 0) {
						const $id = document.getElementById(linkLocation.slice(1));
						if ($id) {
							scrollTo(400, offset($id).top);
						}
					} else {
						this.props.goToPage(linkLocation);
					}
				}
			});

			addEvent(window, 'keydown.general', (e) => {
				if (e.key === 'Escape') {
					if (this.state.cartOpen) this.setState({ cartOpen: false });
					if (this.state.location === true) this.setState({ location: false });
					if (this.state.grid) this.setState({ grid: false });
				} else if (e.shiftKey && e.ctrlKey && e.key === 'A') {
					// ctrl + shift + a = Toggle grid
					this.setState((prevState) => ({ grid: !prevState.grid }));
				}
			});
		}
	}

	componentWillUnmount() {
		removeEvent(document.getElementById('root'), 'click.linkCapture');
		removeEvent(window, 'keydown.general');
		removeEvent(window, 'scroll.logoHeader');
		removeEvent(window, 'resize.logoHeader');
		removeEvent(window, 'message.acuityListener');
	}

	openLocation = (e) => {
		e.preventDefault();

		this.setState((prevState) => ({ location: !prevState.location }));
	}

	changeLanguage = (e) => {
		const changeTo = e.currentTarget.dataset.language;
		this.props.changeLanguage(changeTo);
		this.setState({
			location: false,
		});
	}

	toggleCartHandler = (e) => {
		let toggle = true;
		if (e?.currentTarget && (e.currentTarget.id === 'js-cartButtonHeader' || (e.currentTarget.href && e.currentTarget.getAttribute('href').indexOf('#') === 0))) {
			e.preventDefault();
			if (e.currentTarget.getAttribute('href') === '#close-cart') toggle = false;
			removeClass(document.body, 'u_addedToBag');
		} else {
			this.closeSearchHandler();
		}

		this.setState((prevState) => {
			if (prevState.cartOpen || (!toggle && !prevState.cartOpen)) {
				setTimeout(() => {
					this.setState({
						cartClose: false,
					});
				}, 800);

				removeClass(document.body, 'u_addedToBag');

				return {
					cartOpen: false,
					cartClose: true,
				};
			}

			return {
				menuPopupHeight: window.innerHeight - stickyOffset,
				cartOpen: true,
			};
		});
	}

	closeCartHandler = () => {
		this.setState({ cartOpen: false });
	}

	toggleSearchHandler = () => {
		const { setup, location } = this.props;

		this.setState((prevState) => {
			if (location.type === 'SEARCH') {
				let searchReturn = prefix(setup, '');
				if (location.prev && location.prev.type !== 'SEARCH' && location.prev.pathname) searchReturn = location.prev.pathname;

				if (prevState.showSearch) this.props.goToPage(searchReturn.replace(prefix(setup, ''), ''));
			}
			return { showSearch: !prevState.showSearch };
		});
	}

	closeSearchHandler = () => {
		const { setup, location } = this.props;

		this.setState({ showSearch: false });

		if (location.type === 'SEARCH') {
			let searchReturn = prefix(setup, '');
			if (location.prev && location.prev.type !== 'SEARCH' && location.prev.pathname) searchReturn = location.prev.pathname;

			this.props.goToPage(searchReturn.replace(prefix(setup, ''), ''));
		}
	}

	notificationRemovedHandler = () => {
		setTimeout(() => {
			const menuOffset = offset(this.headerElement.current).top + stickyOffset;

			this.setState({
				menuOffset,
			});
		}, 500);
	}

	resetFilterHandler = () => {
		this.props.resetFilter();
	}

	setProgressHandler = () => {
		this.setState({
			showProgress: true,
		});
	}

	removeProgressHandler = () => {
		this.setState({
			showProgress: false,
		});
	}

	render() {
		const { properties, navigation, setup, header, checkout, location } = this.props;
		const { grid, menuPopupHeight, cartOpen, cartClose, showProgress } = this.state;
		const { totals = {} } = checkout.selection;

		if (properties.siteInitiated === false || properties.siteClosed === true) return null;
		const countryOptions = Object.keys(setup.countries).map((country) => ({ key: country, value: setup.countries[country].name })).sort((a, b) => a.value.localeCompare(b.value));

		return (
			<>
				{ setup.unableToSendToCountry && header.countryError && <Notification type="popup" content={header.countryError} setup={setup} changeCountry={this.props.changeCountry} /> }
				{ header.campaign && <Notification type="campaign" content={header.campaign} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} /> }
				{ header.cookies && <Notification type="cookies" content={header.cookies} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} /> }
				{ header.popup && <Notification type="popup" content={header.popup} setup={setup} notificationRemoved={this.notificationRemovedHandler} removeNotification={this.props.removeNotification} /> }
				<header className={[s.header, cartOpen ? s.header__cartOpen : null].join(' ')} ref={this.headerElement}>
					<div className={s.header_fixedContainer}>
						<div className={s.header_links} ref={this.linksElement}>
							<Navigation navigation={navigation} setup={setup} menuPopupHeight={menuPopupHeight} closeSearch={this.closeSearchHandler} closeCart={this.closeCartHandler} countryOptions={countryOptions} changeCountry={this.props.changeCountry} />
							<Link className={s.header_logo} to={prefix(setup)} onClick={this.closeSearchHandler}>
								<svg width="252" height="44" viewBox="0 0 252 44" xmlns="http://www.w3.org/2000/svg">
									<path d="M11.2949 0.809412L10.6898 0C3.93304 5.26118 0 13.0518 0 21.5506C0 29.9482 3.93304 37.84 10.6898 43L11.2949 42.1906C4.73982 37.1318 1.00847 29.6447 1.00847 21.5506C1.00847 13.4565 4.73982 5.86824 11.2949 0.809412ZM34.5906 34.5012H44.7761V33.9953C41.7507 33.8941 41.2465 32.68 41.2465 28.8353V13.8612C41.2465 11.9388 41.3473 10.9271 41.9524 10.0165C42.4566 9.30824 43.2634 9.00471 44.7761 8.90353V8.49882H37.2126L28.9431 30.0494L20.6737 8.49882H13.1101V9.00471C16.1355 9.10588 16.5389 10.32 16.5389 14.1647V28.7341C16.5389 30.6565 16.4381 32.0729 15.833 32.8824C15.3288 33.5906 14.522 33.8941 13.0093 33.9953V34.5012H21.0771V33.9953C18.1525 33.8941 17.6483 32.68 17.6483 28.8353V10.7247H18.1525L18.2533 10.9271L27.2287 34.6024H28.338L37.5151 10.9271H38.0194V28.9365C38.0194 32.8824 37.5151 34.0965 34.4897 34.0965V34.5012H34.5906ZM57.8863 21.5506C57.8863 13.1529 54.0541 5.26118 47.1965 0.101176L46.5914 0.910588C53.1465 5.96941 56.8778 13.5576 56.8778 21.6518C56.8778 29.7459 53.1465 37.3341 46.5914 42.3929L47.1965 43.2024C54.0541 37.7388 57.8863 29.9482 57.8863 21.5506ZM82.9972 25.2941H72.9125L73.0133 24.9906C74.7277 20.0329 77.7532 11.2306 77.7532 11.2306L77.9548 10.5224L78.1565 11.2306C78.1565 11.2306 80.8794 19.3247 82.8964 24.9906L82.9972 25.2941ZM91.7709 34.5012V33.9953C90.6616 33.7929 89.9557 33.4894 89.4514 32.8824C88.5438 31.8706 87.737 29.5435 87.2328 28.0259L87.1319 27.6212L80.476 8.09412H77.3498L70.3913 28.7341C69.5845 31.1624 68.5761 33.8941 65.4498 34.0965V34.6024H74.526V34.0965C72.2066 34.0965 71.0972 33.2871 71.0972 31.7694C71.0972 31.1624 71.3998 30.2518 72.1057 28.3294C72.1057 28.2282 72.3074 27.6212 72.7108 26.6094V26.4071H83.4006L83.5014 26.6094C84.4091 29.3412 84.8125 30.4541 84.8125 30.5553C84.9133 30.8588 85.2158 31.7694 85.2158 32.2753C85.2158 34.0965 83.2997 34.0965 82.0896 34.0965C81.9887 34.0965 81.9887 34.0965 81.8879 34.0965C81.787 34.0965 81.6862 34.0965 81.5853 34.0965V34.6024H91.7709V34.5012ZM126.462 9.00471V8.49882H118.395V9.00471C121.319 9.10588 121.823 10.32 121.823 14.1647V28.7341L107.201 8.49882H100.847V8.70118V9.00471H101.15C101.856 9.00471 102.662 9.10588 103.066 9.20706C103.57 9.40941 104.074 9.81412 104.578 10.5224V10.6235V28.8353C104.578 32.68 104.074 33.9953 101.15 33.9953V34.5012H109.217V33.9953C106.293 33.8941 105.789 32.68 105.789 28.8353V11.9388L106.192 12.5459L122.126 34.5012H123.034V14.1647C123.034 12.2424 123.134 10.9271 123.739 10.0165C124.244 9.30824 125.05 9.00471 126.462 9.00471ZM151.775 25.2941H141.69L141.791 24.9906C143.505 20.0329 146.531 11.2306 146.531 11.2306L146.733 10.5224L146.934 11.2306C146.934 11.2306 149.657 19.3247 151.674 24.9906L151.775 25.2941ZM160.65 34.5012V33.9953C159.54 33.7929 158.834 33.4894 158.33 32.8824C157.422 31.8706 156.616 29.5435 156.111 28.0259L156.011 27.6212L149.254 8.09412H146.128L139.169 28.7341C138.362 31.1624 137.354 33.8941 134.228 34.0965V34.6024H143.304V34.0965C140.984 34.0965 139.875 33.2871 139.875 31.7694C139.875 31.1624 140.178 30.2518 140.883 28.3294C140.883 28.2282 141.085 27.6212 141.489 26.6094V26.4071H152.178L152.279 26.6094C153.187 29.3412 153.59 30.4541 153.59 30.5553C153.691 30.8588 153.994 31.7694 153.994 32.2753C153.994 34.0965 152.077 34.0965 150.867 34.0965C150.766 34.0965 150.766 34.0965 150.666 34.0965C150.565 34.0965 150.464 34.0965 150.363 34.0965V34.6024H160.65V34.5012ZM172.751 13.8612C172.751 11.0282 174.97 8.80235 177.894 8.80235C182.231 8.80235 185.256 12.9506 186.366 14.7718L186.668 14.5694L184.651 8.6C184.147 9.10588 183.34 9.10588 183.038 9.10588C182.332 9.10588 182.029 9.00471 181.525 8.80235C181.424 8.80235 181.222 8.70118 181.021 8.70118C179.911 8.29647 179.306 8.09412 178.096 8.09412C175.877 8.09412 173.961 8.80235 172.65 10.0165C171.238 11.3318 170.533 13.1529 170.533 15.2776C170.533 20.1341 174.466 21.3482 178.298 22.5624C182.029 23.7765 185.559 24.8894 185.559 29.1388C185.559 30.6565 184.954 31.9718 183.844 32.8824C182.735 33.7929 181.424 34.2988 179.911 34.2988C176.785 34.2988 173.86 32.1741 170.533 27.9247L170.028 28.1271L172.449 34.6024C173.054 34.1977 173.76 33.8941 174.365 33.8941C175.071 33.8941 175.777 34.0965 176.583 34.2988H176.684C177.693 34.6024 178.903 34.9059 180.214 34.9059C184.853 34.9059 188.181 31.7694 188.181 27.4188C188.181 24.9906 187.374 23.2706 185.458 21.9553C183.844 20.7412 181.525 20.1341 179.407 19.4259L178.903 19.3247C175.877 18.3129 172.751 16.9976 172.751 13.8612ZM200.081 34.5012H211.779V33.9953H211.174C207.947 33.9953 207.443 32.7812 207.443 28.8353V14.2659C207.443 12.3435 207.543 10.9271 208.149 10.1176C208.653 9.30824 209.56 9.00471 211.174 9.00471H211.779V8.49882H199.98V9.00471H200.585C203.711 9.00471 204.316 10.4212 204.316 14.2659V28.7341C204.316 30.7576 204.215 31.9718 203.711 32.8824C203.207 33.6918 202.299 33.9953 200.686 33.9953H200.081V34.5012ZM251.311 10.2188V8.49882H235.982L234.47 14.6706L234.974 14.7718C235.478 13.3553 236.184 12.2424 236.991 11.5341C238.1 10.5224 239.815 10.4212 241.327 10.4212C241.529 10.4212 241.63 10.4212 241.831 10.4212H242.235H249.798L248.689 12.1412C246.975 14.9741 244.252 19.3247 243.042 21.7529C240.621 26.5082 239.411 30.2518 239.411 32.8824C239.411 34.2988 239.915 35.0071 241.126 35.0071C241.63 35.0071 242.033 34.9059 242.336 34.6024C242.941 33.9953 242.941 32.7812 242.941 31.7694V31.4659C243.042 24.08 247.076 16.8965 251.311 10.2188Z" />
								</svg>
							</Link>
							<div className={s.functions}>
								<a className={[s.functions_search, this.state.showSearch ? s.functions_search__open : null].join(' ')} onClick={this.toggleSearchHandler} href="#toggle-search">
									<span className={s.functions_search_title}>{ translate(setup, 'search') }</span>
									<span className={s.functions_search_icon}>
										{
											this.state.showSearch ? (
												<svg width="13" height="14" viewBox="0 0 13 14" xmlns="http://www.w3.org/2000/svg">
													<line x1="0.141421" y1="0.858579" x2="12.1414" y2="12.8586" stroke="black" strokeWidth="0.4" />
													<line x1="12.1414" y1="1.14142" x2="0.141422" y2="13.1414" stroke="black" strokeWidth="0.4" />
												</svg>
											) : (
												<svg width="13" height="12" viewBox="0 0 13 12" xmlns="http://www.w3.org/2000/svg">
													<g fill="none" stroke="#000" strokeWidth="0.5" transform="translate(.2 .2)">
														<line x1="8.366" x2="11.941" y1="8.083" y2="11.659" />
														<circle cx="4.8" cy="4.8" r="4.8" />
													</g>
												</svg>
											)
										}
									</span>
								</a>

								{ cartClose && <div className={s.cartShield} /> }
								<div className={[s.cart, cartOpen ? s.cart__open : null].join(' ')} ref={this.cartElement} onMouseEnter={this.setProgressHandler} onMouseLeave={this.removeProgressHandler}>
									<Link id="js-cartButtonHeader" className={s.functions_selection} to={prefix(setup, 'selection')} onClick={this.toggleCartHandler}>
										<span className={s.functions_bag_title}>{ translate(setup, 'bag').replace(/%.*%/, (totals.totalQuantity && totals.totalQuantity > 0 ? `(${totals.totalQuantity})` : '')) }</span>
										<span className={[s.functions_bag_icon, cartOpen ? s.functions_bag_icon__open : null].join(' ')}>
											<span className={s.functions_bag_icon_totals}>{(totals.totalQuantity && totals.totalQuantity > 0 ? totals.totalQuantity : '')}</span>
											<svg xmlns="http://www.w3.org/2000/svg" width="14" height="13" viewBox="0 0 14 13">
												<g fill="none" stroke="#000" strokeWidth="0.5" transform="translate(.2 .2)">
													<path d="M6.8,0 C7.79411255,3.94290921e-17 8.6,0.80588745 8.6,1.8 L8.6,2 L8.6,2 L5,2 L5,1.8 C5,0.80588745 5.80588745,4.04660118e-16 6.8,0 Z" />
													<rect width="13.6" height="10.6" y="2" />
												</g>
											</svg>
										</span>
									</Link>
									<div className={s.cart_inner}>
										<Cart menuPopupHeight={menuPopupHeight} toggleCartHandler={this.toggleCartHandler} cartOpen={cartOpen || showProgress} />
									</div>
								</div>
								<button className={s.cartScreen} type="button" aria-label="Close Cart" onClick={this.toggleCartHandler} />
							</div>
						</div>
					</div>
				</header>
				{
					(this.state.showSearch || location.type === 'SEARCH') && (
						<div className={[s.search, (location.type === 'SEARCH' ? s.search__active : null)].join(' ')}>
							<div className={s.searchScreen} />
							<Search closeSearch={this.closeSearchHandler} />
						</div>
					)
				}
				{ grid && <div className={s.grid}><div /></div> }
			</>
		);
	}
}

const mapState = ({ environment, checkout, location }) => {
	const { properties, navigation, setup, header, pageType } = environment;
	return { properties, navigation, setup, header, pageType, checkout, location };
};

export default hot(module)(connect(mapState, {
	removeNotification,
	clearFilter,
	changeCountry,
	changeAddressInput,
	changeLanguage,
	goToPage,
	clientInit,
	changeDevice,
})(Header));
